import type { FeatureFlags } from '@/stores/feature-flags'
import moment from 'moment'

/**
 * Converts a Date object (or date string/number) to a Unix timestamp in seconds.
 */
export function toUnixSeconds(date: Date | string | number): number {
  return Math.floor(new Date(date).getTime() / 1000)
}

// Get Javascript dates from flat picker result string
export function datesFromFlatPicker(flatPickerStr) {
  const regex = new RegExp(/ to /, 'm')
  let startDate
  let endDate

  const index = flatPickerStr.search(regex)
  if (index > -1) {
    // a single date selected?
    startDate = flatPickerStr.substring(0, index)
    endDate = flatPickerStr.substring(index + 4, flatPickerStr.length)
  } else {
    startDate = flatPickerStr
    endDate = flatPickerStr
  }

  return {
    from: new Date(startDate),
    to: new Date(endDate)
  }
}

export function getPickerTimeRange(timeRange: string) {
  let start_time = 0
  let today_start = new Date(new Date().setHours(0, 0, 0, 0))
  let end_time = toUnixSeconds(Date.now())
  if (timeRange == 'yesterday') {
    start_time = toUnixSeconds(today_start.setDate(today_start.getDate() - 1))
    today_start = new Date(new Date().setHours(0, 0, 0, 0))
    end_time = toUnixSeconds(today_start)
  } else if (timeRange === 'last_7days') {
    start_time = toUnixSeconds(today_start.setDate(today_start.getDate() - 7))
    today_start = new Date(new Date().setHours(0, 0, 0, 0))
    end_time = toUnixSeconds(today_start)
  } else if (timeRange === 'thismonth') {
    start_time = toUnixSeconds(today_start.setDate(1))
  } else if (timeRange === 'lastmonth') {
    const last_month_start = new Date(today_start.setMonth(today_start.getMonth() - 1)).setDate(1)
    start_time = toUnixSeconds(last_month_start)
    today_start = new Date(new Date().setHours(0, 0, 0, 0)) // this is important as today_start changed ^^
    const last_month_end = new Date(today_start.setDate(1)).setDate(1)
    end_time = toUnixSeconds(last_month_end)
  } else if (timeRange === '3months') {
    start_time = toUnixSeconds(today_start.setMonth(today_start.getMonth() - 3))
  } else if (timeRange === '6months') {
    start_time = toUnixSeconds(today_start.setMonth(today_start.getMonth() - 6))
  } else if (timeRange === '1year') {
    start_time = toUnixSeconds(today_start.setMonth(today_start.getMonth() - 12))
  } else if (timeRange === 'yeartodate') {
    today_start.setMonth(0)
    today_start.setDate(1)
    start_time = toUnixSeconds(today_start)
  } else if (timeRange === '2years') {
    start_time = toUnixSeconds(today_start.setMonth(today_start.getMonth() - 24))
  } else if (timeRange === 'max') {
    start_time = 0
  }
  return {
    start_time,
    end_time
  }
}

export function getPickerTimeRanges(timeRange: string, now: Date, venueCreateDate: Date) {
  const dateRanges: { start_time: number; end_time: number }[] = []
  let iterations = 0
  switch (timeRange) {
    case '6months':
      iterations = 6
      break
    case '1year':
      iterations = 12
      break
    case 'yeartodate': {
      const currentYear = now.getFullYear()
      while (now.getFullYear() === currentYear) {
        dateRanges.push({
          start_time: toUnixSeconds(new Date(now.getFullYear(), now.getMonth(), 1)),
          end_time: toUnixSeconds(new Date(now.getFullYear(), now.getMonth() + 1, 0))
        })
        now.setMonth(now.getMonth() - 1)
      }
      return dateRanges
    }
    case '2years':
      iterations = 24
      break
    case 'max': {
      const then = new Date(venueCreateDate.getFullYear(), venueCreateDate.getMonth())
      while (then <= now) {
        dateRanges.push({
          start_time: toUnixSeconds(new Date(now.getFullYear(), now.getMonth(), 1)),
          end_time: toUnixSeconds(new Date(now.getFullYear(), now.getMonth() + 1, 0))
        })
        now.setMonth(now.getMonth() - 1)
      }
      return dateRanges
    }
    default:
      return dateRanges
  }
  for (let i = 0; i < iterations; i++) {
    dateRanges.push({
      start_time: toUnixSeconds(new Date(now.getFullYear(), now.getMonth(), 1)),
      end_time: toUnixSeconds(new Date(now.getFullYear(), now.getMonth() + 1, 0))
    })
    now.setMonth(now.getMonth() - 1)
  }
  return dateRanges
}

export function isOtcItem(featureFlags?: FeatureFlags, item?: any) {
  if (featureFlags?.otcPricing) {
    // If uom_unit of uom_case has been set, it's an otc item so the user can't edit certain properties otherwise the price pulled from sales API will be inaccurate
    return !!item?.uom_unit || !!item?.uom_case
  }
  return false
}

/**
 * A helper for adding an 's' at the end of a word, if necessary.

 * @returns {string} - Returns the original word if count is 1, else it appends an 's' at the end of the word.
 */
export function pluralizeWord(word: string, count: number): string {
  if (count === 1) return word
  if (
    word.endsWith('s') ||
    word.endsWith('x') ||
    word.endsWith('z') ||
    word.endsWith('sh') ||
    word.endsWith('ch')
  ) {
    return word + 'es'
  }
  return word + 's'
}

export const convert24HoursTo12Hours = (inputTime: string | number) => {
  const m = moment(inputTime, ['HH:mm:ss', 'HH:mm'])
  if (m.minutes() === 0) {
    return m.format('h A')
  } else {
    return m.format('h:mm A')
  }
}

export const convertDateToLongDateTimeFormat = (inputDate: number) => {
  const date = moment.unix(inputDate).format('ddd, MMM D, YYYY, h:mm A')
  return date
}

export const formatLongDateTimeNoDay = (inputDate: number) => {
  const date = moment.unix(inputDate).format('MMM D, YYYY, h:mm A')
  return date
}

export const convertLongDateTimeFormatToDate = (inputDateString: string) => {
  if (moment(inputDateString, 'ddd, MMM D, YYYY, h:mm A', true).isValid() === false) {
    return 0
  }
  const date = moment(inputDateString, 'ddd, MMM D, YYYY, h:mm A')
  return date.unix()
}

export const convertDateToLongDateFormat = (inputDate: number) => {
  return moment.unix(inputDate).format('ddd, MMM D, YYYY')
}

export const getClosestDayFromWeekDays = (dates) => {
  const todayIndex = moment().isoWeekday()

  let closestDate = null
  let closestDateDifference = Infinity

  for (const date of dates) {
    const dateIndex = moment().day(date).isoWeekday()
    const difference = (dateIndex - todayIndex + 7) % 7

    if (difference < closestDateDifference) {
      closestDate = date
      closestDateDifference = difference
    }
  }
  return closestDate
}

/**
 * Formats a number as a percentage string.
 *
 * @param value - The number to format (e.g., 50.5 for 50.5%).
 * @param decimals - (Optional) The number of decimal places to floor to. Defaults to 2.
 * @returns The formatted string (e.g., "50.5%") or special characters for Infinity/NaN.
 */
export const formatPercent = (
  value: number,
  decimals: number = 2,
  isFraction: boolean = false,
  errorValue: string = '∞'
): string => {
  if (Number.isNaN(value)) return ''
  if (!Number.isFinite(value)) return value === Infinity ? errorValue : '-' + errorValue
  let percentage = isFraction ? value * 100 : value
  if (isFraction) percentage += Number.EPSILON
  const factor = Math.pow(10, decimals)
  const result = Math.floor(percentage * factor) / factor
  return `${result}%`
}

export const formatQuantity = (number: number) => {
  return Math.floor(number * 100) / 100
}

export const capitalizeSentenceCase = (inputText: string) => {
  if (!inputText || inputText?.length === 0 || typeof inputText !== 'string') {
    return ''
  }
  return inputText.charAt(0).toUpperCase() + inputText.slice(1)
}

export const calculateMean = (inputArray: number[]) => {
  if (inputArray.length === 0) {
    return 0
  }
  const sum = inputArray.reduce((accum, currentValue) => accum + currentValue, 0)
  return sum / inputArray.length
}

export function formatDateShort(dateValue) {
  if (!dateValue) {
    return '' // Return an empty string if the date is null or undefined
  }

  const dateObj = new Date(dateValue)

  // // Check if the date is valid before formatting
  // if (isNaN(dateObj.getTime())) {
  //   return '' // Return empty string for invalid dates
  // }

  const options = {
    year: 'numeric',
    month: '2-digit', // Ensures MM format (e.g., 08)
    day: '2-digit' // Ensures DD format (e.g., 01)
  }

  return dateObj.toLocaleDateString('en-US', options)
}

export function formatDateMedium(dateValue) {
  // e.g. Sept 9, 2025
  const date = new Date(dateValue)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateLong(dateValue) {
  // e.g. September 9, 2025
  const date = new Date(dateValue)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

export function formatDateTime(dateValue) {
  // e.g. September 9, 2025, 2:30 PM (removes leading zeros from hours)
  const date = new Date(dateValue)
  if (isNaN(date.getTime())) return ''
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  })
}

export const formatCurrencyFunc = (
  amount = 0,
  currency: string | null | undefined = '',
  nullValue = false,
  maximumDecimals = 2
) => {
  nullValue = nullValue || false
  if (!amount && nullValue) {
    return ''
  }
  if (!amount && !nullValue) {
    amount = 0
  }
  const safeCurrency = currency || 'USD'

  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: safeCurrency,
    maximumFractionDigits: maximumDecimals
  }).format(amount)
}

/**
 * Safely parses a JSON string, returning a fallback value if parsing fails.
 */
export function safeJsonParse<T = any>(value: any, fallback: T = {} as T): T {
  if (value === null || value === undefined) return fallback
  if (typeof value === 'string') {
    try {
      return JSON.parse(value)
    } catch {
      return fallback
    }
  }
  return value as T
}
